import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { indexStore } from "../../../store/indexStore";
import { getUniqueCategories } from "../../utils/UtilsFunctions";
import Iterator from "src/Components/utils/Iterator";
import { Combobox } from "src/Components/selectbox/ComboBox";
import AddOptionModel from "./AddOptionModel";
import { Button } from "src/Components/ui/button";
import { Plus, Settings, Filter, Package, FolderTree } from "lucide-react";
import { OptionCard, OptionCardLoading } from "src/Components/Cards/OptionCard";
import Visibility from "src/Components/utils/Visibility";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";

function Options(props) {
  const { branchId } = useParams();
  const {
    nameLang,
    fetchOptionsOfItemsByIdFunction,
    fetchItemsOfOptionsFunction,
    itemIdOptions,
    setItemIdOptions,
    itemsOptions,
    ItemOptions,
    ItemOptionsLoading,
    categories,
    menus,
  } = indexStore();
  const { t } = useTranslation();
  const [itemsSelected, setItemsSelected] = useState(itemsOptions);
  const [category, setCategory] = useState();
  
  useEffect(() => {
    fetchItemsOfOptionsFunction(branchId);
    setItemsSelected(itemsOptions);
  }, [branchId]);

  const onSelect = (value, option) => {
    fetchOptionsOfItemsByIdFunction(value);
    setItemIdOptions(value);
  };

  const onSelectCategory = (value, option) => {
    setCategory(value);
    setItemIdOptions(null);
    const newItems = itemsOptions.filter((item) => {
      return item.category_id === value;
    });
    setItemsSelected(newItems);
  };
  
  const uniqueCategories = getUniqueCategories(categories, menus, nameLang);
  const uniqueItems = getUniqueCategories(itemsSelected, categories, nameLang);
  
  return (
    <div className="flex flex-col h-full gap-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-gradient-to-br from-primary/10 to-transparent border">
            <Settings className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">{t("dashbord.options")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("gloabl.manage_options_desc") || "Manage item options and sub-options"}
            </p>
          </div>
        </div>
        <AddOptionModel
          trigger={
            <Button className="gap-2">
              <Plus className="w-4 h-4" />
              <span className="hidden md:block">
                {t("gloabl.add")} {t("gloabl.option")}
              </span>
              <span className="md:hidden">{t("gloabl.add")}</span>
            </Button>
          }
        />
      </div>

      {/* Filter Card */}
      <Card className="border-2">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted">
              <Filter className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.filter_options") || "Filter Options"}</CardTitle>
              <CardDescription>
                {t("gloabl.filter_options_desc") || "Select category and item to view options"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <FolderTree className="w-4 h-4" />
                {t("gloabl.category")}
              </label>
              <Combobox
                data={uniqueCategories}
                onChange={onSelectCategory}
                value={category}
                placeholder={t("gloabl.select") + " " + t("gloabl.category")}
                lable={"displayName"}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Package className="w-4 h-4" />
                {t("gloabl.item")}
              </label>
              <Combobox
                data={uniqueItems}
                onChange={onSelect}
                value={itemIdOptions || null}
                placeholder={t("gloabl.select") + " " + t("gloabl.item")}
                lable={nameLang}
                disabled={!category}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Options Grid */}
      <div className="flex-1">
        <Visibility visible={ItemOptionsLoading}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Iterator
              data={[1, 2, 3]}
              render={(row) => <OptionCardLoading key={row} />}
            />
          </div>
        </Visibility>
        <Visibility visible={!ItemOptionsLoading}>
          {ItemOptions.length > 0 ? (
            <>
              <div className="mb-4 text-sm text-muted-foreground">
                {t("gloabl.options_found", { count: ItemOptions.length }) || `${ItemOptions.length} ${t("gloabl.option")}${ItemOptions.length > 1 ? "s" : ""}`}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Iterator
                  data={ItemOptions}
                  render={(row) => <OptionCard key={row.id} {...row} />}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
              <Settings className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-xl font-semibold mb-2">{t("gloabl.no_options_found") || "No options found"}</p>
              <p className="mb-4">
                {!itemIdOptions
                  ? t("gloabl.select_item_to_view_options") || "Please select a category and item to view options"
                  : t("gloabl.no_options_for_item") || "No options available for this item"}
              </p>
            </div>
          )}
        </Visibility>
      </div>
    </div>
  );
}

export default Options;
